/**
 * PERFORMANCE MAX WORKSPACE — /google-ads/sales and /google-ads/local
 *
 * Same look as the YouTube and Search workspaces (toolbar · stat tiles ·
 * filter bar · campaign → asset group rows · list / grid), built for
 * Performance Max: each objective lists its own campaigns (what the CRM
 * recorded when it built them; older ones are filed by linked store
 * locations), asset groups show their real images, Google's asset strength
 * and every text line, and the grid previews each campaign's first image.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { BarChart3, Image as ImageIcon, Info, Layers, MapPin, Megaphone, Pause, Play, Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import {
  googleAdsService, GoogleAdsAssetGroupAsset, GoogleAdsAssetGroupRow, GoogleAdsCampaignSummary, PmaxAssetGroupPreview, PmaxCampaignMeta,
} from '@/services/google-ads.service';
import {
  alreadyToasted, biddingLabel, errMsg, formatMicros, newestFirst, Pill, pretty, statusOf, Switch, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { hostOf } from '@/components/google-ads/objectives/objectiveShared';
import {
  CampaignCard, CampaignFilters, ChannelTile, ChildRow, ChipLine, ConnectGate, EmptyWorkspace, Skeleton, StatTiles, useCampaignFilters,
  useCampaignToggle, useViewMode, WorkspaceToolbar,
} from '@/components/google-ads/objectives/workspaceChrome';
import { isPmaxCampaign, PmaxObjectiveConfig } from './pmaxShared';

type Counts = { byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> };

const OTHER: Record<PmaxObjectiveConfig['key'], { title: string; base: string }> = {
  sales: { title: 'Local store visits', base: '/google-ads/local' },
  local: { title: 'Sales', base: '/google-ads/sales' },
};

/** Google's asset-group ad strength → pill. */
function strengthPill(s?: string): { label: string; tone: 'ok' | 'warn' | 'bad' | 'paused' } | null {
  switch ((s ?? '').toUpperCase()) {
    case 'EXCELLENT': return { label: 'Excellent', tone: 'ok' };
    case 'GOOD': return { label: 'Good', tone: 'ok' };
    case 'AVERAGE': return { label: 'Average', tone: 'warn' };
    case 'POOR': return { label: 'Poor', tone: 'bad' };
    case 'PENDING': return { label: 'Strength pending', tone: 'paused' };
    default: return null;
  }
}

// ── asset groups ─────────────────────────────────────────────────────────

const TEXT_FIELDS = [['HEADLINE', 'Headlines'], ['LONG_HEADLINE', 'Long headlines'], ['DESCRIPTION', 'Descriptions'], ['BUSINESS_NAME', 'Business name']] as const;

function AssetDetail({ list }: { list: GoogleAdsAssetGroupAsset[] }) {
  const images = list.filter((a) => a.type === 'IMAGE');
  const videos = list.filter((a) => a.type === 'YOUTUBE_VIDEO');
  const cta = list.find((a) => a.fieldType === 'CALL_TO_ACTION_SELECTION');
  return (
    <div className="space-y-2.5 py-3 pl-9">
      {TEXT_FIELDS.map(([ft, label]) => (
        <ChipLine key={ft} label={label} items={list.filter((a) => a.fieldType === ft && a.text).map((a) => a.text as string)} max={15} />
      ))}
      {images.length > 0 && (
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Images & logos · {images.length}</div>
          <div className="flex flex-wrap gap-2">
            {images.map((a) => (
              <a key={`${a.fieldType}:${a.assetId}`} href={a.imageUrl} target="_blank" rel="noreferrer" title={`${pretty(a.fieldType)}${a.width && a.height ? ` · ${a.width}×${a.height}` : ''}`}
                className="group/img relative overflow-hidden rounded-lg border border-border bg-surface-alt">
                {a.imageUrl
                  ? <img src={a.imageUrl} alt="" referrerPolicy="no-referrer" className={clsx('h-16 w-auto max-w-[140px]', /LOGO/.test(a.fieldType) ? 'bg-white object-contain p-1' : 'object-cover')} />
                  : <span className="flex h-16 w-24 items-center justify-center text-2xs text-text-muted">no preview</span>}
                <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[9px] leading-tight text-white opacity-0 transition-opacity group-hover/img:opacity-100">{pretty(a.fieldType)}</span>
                {a.source === 'AUTOMATICALLY_CREATED' && (
                  <span className="absolute right-1 top-1 rounded bg-black/65 px-1 text-[9px] font-medium text-white" title="Created by Google">Auto</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}
      {videos.length > 0 && (
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Videos · {videos.length}</div>
          <div className="flex flex-wrap gap-2">
            {videos.map((a) => (
              <a key={a.assetId} href={a.youtubeVideoId ? `https://youtu.be/${a.youtubeVideoId}` : undefined} target="_blank" rel="noreferrer"
                className="relative overflow-hidden rounded-lg border border-border" title={a.youtubeVideoTitle ?? a.youtubeVideoId}>
                {a.youtubeVideoId ? <img src={`https://i.ytimg.com/vi/${a.youtubeVideoId}/mqdefault.jpg`} alt="" className="h-16 w-auto object-cover" /> : <span className="flex h-16 w-28 items-center justify-center text-2xs text-text-muted">video</span>}
                <span className="absolute inset-0 flex items-center justify-center"><Play size={16} className="fill-white text-white drop-shadow" /></span>
              </a>
            ))}
          </div>
        </div>
      )}
      {cta?.text && <ChipLine label="Call to action" items={[pretty(cta.text)]} />}
    </div>
  );
}

function AssetGroupRows({ accountUid, campaign }: { accountUid: string; campaign: GoogleAdsCampaignSummary }) {
  const [groups, setGroups] = useState<GoogleAdsAssetGroupRow[] | null>(null);
  const [assets, setAssets] = useState<Record<string, GoogleAdsAssetGroupAsset[] | null>>({});
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError('');
    try {
      const gs = await googleAdsService.performanceMax.listAssetGroups(accountUid, campaign.id);
      setGroups(gs);
      // Each group's assets up front, so the row can show its thumbnail and "5 headlines · 4 images" without a click.
      gs.forEach((g) => {
        googleAdsService.performanceMax.listAssetGroupAssets(accountUid, g.id)
          .then((a) => setAssets((prev) => ({ ...prev, [g.id]: a })))
          .catch(() => setAssets((prev) => ({ ...prev, [g.id]: [] })));
      });
    } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAssetGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.performanceMax.pauseAssetGroup(accountUid, g.id);
      else await googleAdsService.performanceMax.resumeAssetGroup(accountUid, g.id);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  const summarise = (list: GoogleAdsAssetGroupAsset[] | null | undefined, finalUrl?: string) => {
    if (!list) return 'Loading assets…';
    const n = (pred: (a: GoogleAdsAssetGroupAsset) => boolean) => list.filter(pred).length;
    const parts = ([
      [n((a) => a.fieldType === 'HEADLINE'), 'headline'],
      [n((a) => a.fieldType === 'DESCRIPTION'), 'description'],
      [n((a) => a.type === 'IMAGE' && !/LOGO/.test(a.fieldType)), 'image'],
      [n((a) => /LOGO/.test(a.fieldType)), 'logo'],
      [n((a) => a.type === 'YOUTUBE_VIDEO'), 'video'],
    ] as Array<[number, string]>).filter(([c]) => c > 0).map(([c, l]) => `${c} ${l}${c === 1 ? '' : 's'}`);
    return [...(parts.length ? parts : ['no assets yet']), finalUrl ? hostOf(finalUrl) : null].filter(Boolean).join(' · ');
  };

  if (error) return <div className="py-2 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (groups === null) return <Skeleton count={2} indent="pl-8" />;
  return (
    <div className="space-y-1 pl-2">
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No asset groups yet — the campaign can't serve without one.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        const list = assets[g.id];
        const thumb = list?.find((a) => a.fieldType === 'MARKETING_IMAGE' && a.imageUrl)?.imageUrl ?? list?.find((a) => a.type === 'IMAGE' && !/LOGO/.test(a.fieldType) && a.imageUrl)?.imageUrl;
        const strength = strengthPill(g.adStrength);
        return (
          <ChildRow key={g.id} open={open} onToggle={() => setOpenId(open ? null : g.id)}
            lead={thumb
              ? <img src={thumb} alt="" referrerPolicy="no-referrer" className="h-8 w-14 shrink-0 rounded object-cover" />
              : <span className="flex h-8 w-14 shrink-0 items-center justify-center rounded bg-surface-alt text-text-muted"><Layers size={14} /></span>}
            title={g.name}
            sub={<>Asset group · {summarise(list, g.finalUrls[0])}</>}
            actions={strength && <Pill {...strength} className="hidden md:inline-flex" />}
            status={<><Pill {...st} /><Switch checked={g.status === 'ENABLED'} disabled={busy === g.id || g.status === 'REMOVED'} label={`Enable ${g.name}`} onCheckedChange={() => toggle(g)} /></>}>
            {list === undefined || list === null ? <Skeleton count={2} indent="pl-9" /> : list.length === 0
              ? <div className="py-3 pl-9 text-xs text-text-muted">No assets linked to this group.</div>
              : <AssetDetail list={list} />}
          </ChildRow>
        );
      })}
    </div>
  );
}

// ── workspace ────────────────────────────────────────────────────────────

export default function PmaxWorkspace({ objective: cfg }: { objective: PmaxObjectiveConfig }) {
  const navigate = useNavigate();
  const { auth, account, accountUid, checkAuth } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const customerId = account?.customerId ?? '';
  const currency = auth?.currency ?? null;
  const newRoute = `${cfg.base}/create/campaign`;

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [meta, setMeta] = useState<Record<string, PmaxCampaignMeta>>({});
  const [counts, setCounts] = useState<Counts | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useViewMode(`gads_${cfg.key}_view`);
  const [previews, setPreviews] = useState<PmaxAssetGroupPreview[] | null>(null);

  const load = useCallback(async () => {
    if (!ready) return;
    setListError(null);
    setRefreshing(true);
    try {
      const [list, m] = await Promise.all([
        googleAdsService.listCampaigns(accountUid),
        googleAdsService.performanceMax.campaignMeta(accountUid).catch(() => ({} as Record<string, PmaxCampaignMeta>)),
      ]);
      setMeta(m);
      setCampaigns(newestFirst(list.filter((c) => isPmaxCampaign(c) && (m[c.id]?.objective ?? 'sales') === cfg.key)));
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
    googleAdsService.campaignCounts(accountUid).then(setCounts).catch(() => undefined);
  }, [ready, accountUid, cfg.key]);
  // customerId: re-attaching a different customer on the same account must refetch.
  useEffect(() => { load(); }, [load, customerId]);

  const f = useCampaignFilters(campaigns);
  const { busy, toggle } = useCampaignToggle(accountUid, load);

  useEffect(() => {
    if (view !== 'grid' || !accountUid || previews !== null) return;
    googleAdsService.performanceMax.assetGroupPreviews(accountUid).then(setPreviews).catch(() => setPreviews([]));
  }, [view, accountUid, previews]);
  const previewOf = useMemo(() => {
    const m = new Map<string, PmaxAssetGroupPreview>();
    for (const p of previews ?? []) { const cur = m.get(p.campaignId); if (!cur || (!cur.imageUrl && p.imageUrl)) m.set(p.campaignId, p); }
    return m;
  }, [previews]);

  const subline = (c: GoogleAdsCampaignSummary) => {
    const m = meta[c.id];
    const groups = counts?.byCampaign[c.id]?.assetGroups;
    const parts = [
      biddingLabel(c.biddingStrategyType),
      m?.targetCpaMicros ? `target CPA ${formatMicros(m.targetCpaMicros, currency)}` : null,
      m?.targetRoas ? `target ROAS ${Math.round(m.targetRoas * 100)}%` : null,
      formatMicros(c.budgetAmountMicros, currency) && `${formatMicros(c.budgetAmountMicros, currency)} / day`,
    ].filter(Boolean);
    return (
      <>
        <span className="truncate">{parts.join(' · ') || 'Performance Max'}</span>
        {groups !== undefined && <span className="inline-flex items-center gap-1"><Layers size={12} /> {groups} asset group{groups === 1 ? '' : 's'}</span>}
        {m && m.locationSets > 0 && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.locationSets} store group{m.locationSets === 1 ? '' : 's'}</span>}
      </>
    );
  };

  const totalGroups = f.live.reduce((n, c) => n + (counts?.byCampaign[c.id]?.assetGroups ?? 0), 0);
  const Icon = cfg.icon;
  const stats = [
    { label: 'Campaigns', value: f.live.length, Icon: Megaphone },
    { label: 'Enabled', value: f.live.filter((c) => c.status === 'ENABLED').length, Icon: Play },
    { label: 'Paused', value: f.live.filter((c) => c.status === 'PAUSED').length, Icon: Pause },
    counts ? { label: 'Asset groups', value: totalGroups, Icon: Layers }
      : { label: 'Daily budget (enabled)', value: formatMicros(f.live.filter((c) => c.status === 'ENABLED').reduce((n, c) => n + Number(c.budgetAmountMicros || 0), 0), currency) ?? '0', Icon: BarChart3 },
  ];

  return (
    <>
      <Topbar title={cfg.title} breadcrumb={`Google Ads / ${cfg.title}`} description={cfg.subtitle} />

      <div className="gads-workspace w-full px-5 py-4">
        {auth === undefined && <div className="space-y-3"><div className="skeleton h-14 rounded-2xl" /><Skeleton count={3} /></div>}
        {auth !== undefined && !ready && <ConnectGate auth={auth ?? { connected: false }} accountUid={accountUid} onChanged={checkAuth} />}

        {ready && (
          <div className="w-full pb-10">
            <WorkspaceToolbar auth={auth} onCustomer={() => setPickerOpen(true)} onRefresh={() => { setPreviews(null); load(); }} refreshing={refreshing}
              view={view} onView={setView} onNew={() => navigate(newRoute)} />
            <StatTiles stats={stats} loading={campaigns === null || !!listError} />
            <CampaignFilters f={f} />

            <div className="space-y-3">
              {listError && <Card role="alert" className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}<button type="button" className="ml-3 underline" onClick={load}>Try again</button></Card>}
              {campaigns === null && !listError && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-2xl" />)}</div>}

              {campaigns !== null && f.live.length === 0 && f.removed.length === 0 && !listError && (
                <EmptyWorkspace icon={Icon} title={`No ${cfg.title.toLowerCase()} campaigns yet`} hint={cfg.emptyHint} onNew={() => navigate(newRoute)}
                  points={cfg.storeGoals
                    ? ['Business Profile locations', 'Maps, Search, YouTube & Display', 'Asset groups', 'Starts paused']
                    : ['One campaign, every Google surface', 'Asset groups', 'Audience signals', 'Starts paused']} />
              )}

              {view === 'list' && f.visible.length > 0 && (
                <div className="gads-campaign-list">
                  {f.visible.map((c) => (
                    <CampaignCard key={c.id} c={c} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                      tile={<ChannelTile icon={Icon} />} subline={subline(c)} busy={busy === c.id} onStatus={() => toggle(c)}
                      tags={meta[c.id] && !meta[c.id].tagged && meta[c.id].objective === 'local' ? <Tag tone="ok" className="shrink-0">store locations</Tag> : undefined}>
                      <AssetGroupRows accountUid={accountUid} campaign={c} />
                      {c.status !== 'REMOVED' && (
                        <div className="pl-9 pt-3">
                          <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                            onClick={() => navigate(`${cfg.base}/create/assetgroup?campaign_id=${c.id}`)}>
                            <Plus size={13} /> New asset group
                          </Button>
                        </div>
                      )}
                    </CampaignCard>
                  ))}
                </div>
              )}

              {view === 'grid' && f.visible.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                  {f.visible.map((c) => {
                    const st = statusOf(c.status, c.primaryStatus);
                    const p = previewOf.get(c.id);
                    return (
                      <Card key={c.id} padded={false} className="flex flex-col overflow-hidden">
                        <div className="relative aspect-[1.91/1] w-full overflow-hidden bg-surface-alt">
                          {p?.imageUrl
                            ? <img src={p.imageUrl} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                            : <div className="flex h-full items-center justify-center bg-primary-soft text-primary">{previews === null ? <ImageIcon size={26} /> : <Icon size={28} />}</div>}
                          <span className="absolute right-2 top-2"><Pill {...st} /></span>
                          {p?.logoUrl && <img src={p.logoUrl} alt="" referrerPolicy="no-referrer" className="absolute left-2 top-2 h-8 w-8 rounded-full border border-white/60 bg-white object-contain" />}
                          {p?.headline && (
                            <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-xs font-medium text-white">{p.headline}</span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col p-3.5">
                          <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 truncate text-xs text-text-muted">{subline(c)}</div>
                          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
                            <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={() => toggle(c)} />
                            <span className="text-xs text-text-muted">{c.status === 'ENABLED' ? 'Enabled' : c.status === 'REMOVED' ? 'Removed' : 'Paused'}</span>
                            <span className="flex-1" />
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setView('list'); setOpenId(c.id); }}>Open</Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {campaigns !== null && (f.live.length > 0 || f.removed.length > 0) && f.visible.length === 0 && (
                <Card className="p-8 text-center text-sm text-text-muted">No campaigns match your filters.</Card>
              )}

              {campaigns !== null && !listError && (
                <p className="flex items-start gap-1.5 px-1 pt-1 text-2xs text-text-muted">
                  <Info size={12} className="mt-px shrink-0" />
                  <span>
                    Performance Max campaigns built in {cfg.title} are filed here; older ones go by whether they link store locations. The rest are under{' '}
                    <Link to={OTHER[cfg.key].base} className="text-primary hover:underline">{OTHER[cfg.key].title}</Link>.
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {ready && (
        <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
          onAttached={async () => { setPickerOpen(false); await checkAuth(); }} />
      )}
    </>
  );
}

